import { useEffect, useState } from "react";
import { ExternalLink, Eye, FileSpreadsheet, FileText, Image as Afbeelding, Paperclip, Presentation } from "lucide-react";
import { useTaal } from "@/contexts/TaalContext";
import DocumentBekijken, { LEESBAAR } from "./DocumentBekijken";
import { opDeMac, openOpDeMac } from "@/lib/op-de-mac";

/**
 * Een bijlage als kaartje in het gesprek. Glasronde 17 oktober 2026, punt 3.
 *
 * GEMETEN: een ingeladen bijlage reist mee met het bericht (`attachments`, met
 * `name`, `type` en de inhoud als base64 in `data`), de backend bewaart haar zo
 * op het bericht en `GET /messages` geeft haar terug. Alleen tekende
 * `MessageBubble` haar nergens, dus na versturen was zij onzichtbaar. Hier staat
 * zij: naam, soort, en een knop die haar opent uit de bewaarde inhoud, zonder
 * netwerk.
 */
export function soortVan(att) {
  const naam = (att?.name || "").toLowerCase();
  const type = att?.type || "";
  if (type.startsWith("image/")) return "Afbeelding";
  if (naam.endsWith(".pdf") || type === "application/pdf") return "PDF";
  if (naam.endsWith(".docx") || naam.endsWith(".doc")) return "Word";
  if (naam.endsWith(".xlsx")) return "Excel";
  if (naam.endsWith(".pptx")) return "PowerPoint";
  // NS-D3: een antwoord als markdown. Hij opent als download (niet in INLINE), zoals elke tekst.
  if (naam.endsWith(".md")) return "Markdown";
  // NS-A1: een HTML-bestand bekijk je in een venster zonder scripts (DocumentBekijken).
  if (naam.endsWith(".html") || naam.endsWith(".htm") || type === "text/html") return "HTML";
  return "Bestand";
}

/** Wat inline mag openen, op het ECHTE type en nooit op de naam. Een blob-adres
 *  heeft de herkomst van de app: een html- of svg-bijlage die inline opent, kan
 *  daar script draaien met de rechten van de app. Alles buiten deze lijst wordt
 *  een download zonder type (securityreview 17 oktober 2026). */
export const INLINE = new Set(["application/pdf", "image/png", "image/jpeg", "image/gif", "image/webp"]);

export const magInline = (att) => INLINE.has(att?.type || "");

/** Een blob-adres voor de bewaarde inhoud, of "" als er geen is. */
export function openAdres(att) {
  if (!att?.data) return "";
  try {
    const ruw = atob(att.data);
    const bytes = new Uint8Array(ruw.length);
    for (let i = 0; i < ruw.length; i += 1) bytes[i] = ruw.charCodeAt(i);
    const type = magInline(att) ? att.type : "application/octet-stream";
    return URL.createObjectURL(new Blob([bytes], { type }));
  } catch {
    return "";
  }
}

const ICOON = { Afbeelding, PDF: FileText, Markdown: FileText, HTML: FileText, Word: FileText, Excel: FileSpreadsheet, PowerPoint: Presentation, Bestand: Paperclip };

/**
 * J7 (glasronde 0.9.172): op de Mac geeft het kaartje het BESTAND aan de schil, die
 * het in de map Downloads schrijft en met de standaard-app opent. Een blob-adres
 * aan het systeem geven werkt niet: macOS meldde dat er geen app voor is. Zonder
 * de schil (browser, telefoon) blijft het de link. De controle zelf staat sinds CA3 in lib/op-de-mac.js.
 */
export { opDeMac, openOpDeMac };

export default function BijlageKaart({ att }) {
  const { t } = useTaal();
  const soort = soortVan(att);
  const [adres, setAdres] = useState("");
  useEffect(() => {
    const a = openAdres(att);
    setAdres(a);
    return () => { if (a) URL.revokeObjectURL(a); };
  }, [att]);
  const Icoon = ICOON[soort] || Paperclip;
  const [bekijk, setBekijk] = useState(false);
  const [melding, setMelding] = useState("");
  const kanBekijken = Boolean(att?.data) && LEESBAAR.has(soort);

  const openMac = async () => {
    const uit = await openOpDeMac(att);
    setMelding(uit.geopend ? t("Geopend vanuit Downloads") : uit.pad ? t("Bewaard in Downloads") : t("Openen lukte niet"));
  };

  return (
    <div className="mt-2 flex items-center gap-2 rounded-lg border bg-background/60 px-2 py-1.5 text-left"
         data-testid="bijlage-kaart">
      <Icoon className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
      <div className="min-w-0 flex-1">
        <p className="truncate text-xs font-medium" data-testid="bijlage-naam">{att?.name || t("Bijlage")}</p>
        <p className="text-[11px] text-muted-foreground" data-testid="bijlage-soort">
          {soort === "Afbeelding" ? t("Afbeelding") : soort === "Bestand" ? t("Bestand") : soort}
        </p>
      </div>
      {kanBekijken && (
        <button type="button" onClick={() => setBekijk(true)}
                className="inline-flex items-center gap-1 text-xs text-[hsl(var(--accent))] underline"
                data-testid="bijlage-bekijk">
          <Eye className="h-3 w-3" aria-hidden="true" />{t("Bekijken")}
        </button>
      )}
      {adres && opDeMac() ? (
        <button type="button" onClick={openMac}
                className="inline-flex items-center gap-1 text-xs text-[hsl(var(--accent))] underline"
                data-testid="bijlage-open">
          <ExternalLink className="h-3 w-3" aria-hidden="true" />{t("Openen")}
        </button>
      ) : adres ? (
        <a href={adres} target="_blank" rel="noopener noreferrer" download={magInline(att) ? undefined : (att.name || "bijlage")}
           className="inline-flex items-center gap-1 text-xs text-[hsl(var(--accent))] underline"
           data-testid="bijlage-open">
          <ExternalLink className="h-3 w-3" aria-hidden="true" />{t("Openen")}
        </a>
      ) : (
        <span className="text-[11px] text-muted-foreground" data-testid="bijlage-geen-inhoud">
          {t("inhoud niet bewaard")}
        </span>
      )}
      {melding && <span className="text-[11px] text-muted-foreground" role="status" data-testid="bijlage-melding">{melding}</span>}
      {kanBekijken && (
        <DocumentBekijken att={att} soort={soort} pdfAdres={soort === "PDF" ? adres : ""} open={bekijk} onOpenChange={setBekijk} />
      )}
    </div>
  );
}
